import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { useCreateAppointment } from "~/api/appointments";
import { getProviders } from "~/api/providers";
import type { Provider } from "~/api/providers";
import { ROUTES } from "~/router/routes";
import { HipaaLogo } from "~/shared/components/icons/HipaaLogo";
import CalendarSelector from "~/shared/components/ui/CalendarSelector";
import { ConfirmActionButtons } from "~/shared/components/ui/ConfirmActionButtons";
import LocationDropdown from "~/shared/components/ui/LocationDropdown";
import ProviderCard from "~/shared/components/ui/ProviderCard";
import SpecialtyDropdown from "~/shared/components/ui/SpecialtyDropdown";
import { useCalendarDays } from "~/shared/hooks/useCalendarDays";
import { useDraftSync } from "~/shared/hooks/useDraftSync";
import { useNow } from "~/shared/hooks/useNow";
import {
  INSURANCE_PROVIDERS,
  NEW_YORK_STATE,
  US_STATES,
  VIDEO_CALL_LOCATION,
} from "~/shared/utils/constants";
import {
  countBookableSlots,
  currentDay,
  currentDayName,
  currentMonth,
  formatAppointmentTime,
  isSlotInPast,
} from "~/shared/utils/dates";
import type {
  ProviderLoadedInfo,
  ProvidersLoadedTrigger,
} from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { useFunnelStore } from "~/stores/useFunnelStore";
import {
  getBookingErrorMessage,
  getStaleSlotMessage,
  isExpiredTokenError,
  isStaleSlotError,
} from "../utils/bookingErrors";
import { isNonProduction } from "../utils/envUtils";
import { funnelTracker } from "../utils/funnelTracking";
import { getUtmParams } from "../utils/utmCapture";
import { AppointmentCreatedScreen } from "./AppointmentCreatedScreen";

const ProviderSelectionScreen: React.FC = () => {
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null,
  );
  const [appointmentId, setAppointmentId] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [showAppointmentCreated, setShowAppointmentCreated] = useState(false);
  const [appointmentLocation, setAppointmentLocation] = useState<string | null>(
    null,
  );
  const [autoSwitchedToTelehealth, setAutoSwitchedToTelehealth] =
    useState(false);

  // Refs for initialization
  const hasInitializedRef = useRef(false);
  const appointmentCreatedRef = useRef(false);

  // Refs to track previous values for trigger detection
  const prevDateRef = useRef<string | null>(null);
  const prevSpecialtiesRef = useRef<string[]>([]);
  const prevLocationRef = useRef<string>("");
  const isInitialLoadRef = useRef(true);

  const {
    patient,
    appointmentBooked,
    setAppointmentBooked,
    setPatientField,
    reset,
  } = useSignUpStore();
  const navigate = useNavigate();
  const sessionId = useFunnelStore((s) => s.session_id);

  // Helper to determine appointment type
  const getAppointmentType = useCallback(
    (): "telehealth" | "in-person" =>
      selectedLocation === VIDEO_CALL_LOCATION ? "telehealth" : "in-person",
    [selectedLocation],
  );

  const handleDateSelection = useCallback((date: Date | null) => {
    setSelectedDate(date);
  }, []);

  // Track calendar date selection
  const handleCalendarDateSelect = useCallback(
    (date: Date) => {
      if (appointmentCreatedRef.current) return;
      const appointmentType = getAppointmentType();
      const newDateStr = date.toISOString().split("T")[0];
      const prevDateStr = selectedDate?.toISOString().split("T")[0];

      // Track with funnel tracker
      funnelTracker.trackCalendarDateSelect(
        newDateStr,
        prevDateStr,
        appointmentType,
      );

      // Store for trigger detection
      prevDateRef.current = prevDateStr ?? null;
      setAutoSwitchedToTelehealth(false);
      setSelectedDate(date);
    },
    [selectedDate, getAppointmentType],
  );

  // Track specialty filter changes
  const handleSpecialtyChange = useCallback(
    (specialties: string[]) => {
      if (appointmentCreatedRef.current) return;
      const prevSpecialties = [...selectedSpecialties];
      funnelTracker.trackFilterChange("specialty", specialties);
      setSelectedSpecialties(specialties);
      setAutoSwitchedToTelehealth(false);
      // Store for trigger detection
      prevSpecialtiesRef.current = prevSpecialties;
    },
    [selectedSpecialties],
  );

  // Track location filter changes
  const handleLocationChange = useCallback(
    (location: string) => {
      if (appointmentCreatedRef.current) return;
      setAutoSwitchedToTelehealth(false); // Reset flag on manual change
      const prevLocation = selectedLocation;
      funnelTracker.trackFilterChange("location", location);
      setSelectedLocation(location);

      // Store for trigger detection
      prevLocationRef.current = prevLocation;
    },
    [selectedLocation],
  );

  // Track time slot selection
  const handleTimeSelect = useCallback((provider: Provider, time: string) => {
    if (appointmentCreatedRef.current) return;
    funnelTracker.trackTimeSlotSelect(
      provider.id,
      provider.fullName,
      time,
      countBookableSlots(provider.availableSlots),
    );
    setSelectedProvider(provider);
    setAutoSwitchedToTelehealth(false);
    setSelectedTime(time);
  }, []);

  const {
    calendarDays,
    canNavigatePrev,
    navigatePrevWeek,
    navigateNextWeek,
    isLoading: isCalendarLoading,
    error: calendarError,
    availabilityForNextMonth,
    isMobile,
  } = useCalendarDays(
    selectedSpecialties,
    selectedLocation,
    handleDateSelection,
  );

  // Track calendar navigation
  const handleNavigatePrev = useCallback(() => {
    if (appointmentCreatedRef.current) return;
    funnelTracker.trackCalendarNavigate("prev");
    navigatePrevWeek();
  }, [navigatePrevWeek]);

  const handleNavigateNext = useCallback(() => {
    if (appointmentCreatedRef.current) return;
    funnelTracker.trackCalendarNavigate("next");
    navigateNextWeek();
  }, [navigateNextWeek]);

  // On refresh after appointment is booked, reset and redirect to sign-up
  useEffect(() => {
    if (appointmentBooked && !showAppointmentCreated) {
      reset();
      navigate(ROUTES.signUp, { replace: true });
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    funnelTracker.startStep("provider_selection");
    if (patient && !hasInitializedRef.current) {
      setSelectedSpecialties(patient.conditions);
      setSelectedLocation(patient.preferredOfficeVisit ?? VIDEO_CALL_LOCATION);
      hasInitializedRef.current = true;
    }
  }, [patient]);

  const {
    data: providers = [],
    isLoading,
    isSuccess,
    error,
  } = useQuery({
    queryKey: [
      "providers",
      selectedSpecialties,
      selectedLocation,
      selectedDate?.toISOString().split("T")[0],
      // The referring practice narrows the roster, so it must split the cache.
      patient.referringPracticeId ?? null,
    ],
    queryFn: () => {
      return getProviders({
        location: selectedLocation,
        startDate: selectedDate?.toISOString().split("T")[0] ?? "",
        page: 1,
        perPage: 10,
        state: US_STATES.find((state) => state.label === patient.state)?.value,
        primaryInsurance:
          INSURANCE_PROVIDERS[
            patient.primaryInsurance
              .providerName as keyof typeof INSURANCE_PROVIDERS
          ],
        secondaryInsurance: patient.secondaryInsurance?.providerName
          ? INSURANCE_PROVIDERS[
              patient.secondaryInsurance
                .providerName as keyof typeof INSURANCE_PROVIDERS
            ]
          : undefined,
        patientEmail: patient.email,
        patientId: patient.id,
        conditions: selectedSpecialties,
        referringPracticeId: patient.referringPracticeId ?? undefined,
      });
    },
    enabled: selectedSpecialties.length > 0 && !!selectedDate,
    // Keep the previously loaded providers on screen while a new date/filter is
    // refetching. Without this the list collapsed to an empty "Loading..." state
    // on every refetch, unmounting the ProviderCards mid-interaction so users
    // reaching for a time slot never landed the click (time_slot_select never
    // fired). Mirrors the daysWithAvailability query in useCalendarDays.
    placeholderData: (previousData) => previousData,
    // The availability data is short-lived but not so volatile that it needs to
    // refetch on every window focus/remount; that background churn was firing
    // the providers query many times in seconds and thrashing the slot UI.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  // Track when providers are loaded with full context
  useEffect(() => {
    if (appointmentCreatedRef.current) return;
    if (selectedDate && !isLoading) {
      const appointmentType = getAppointmentType();
      const currentDateStr = selectedDate.toISOString().split("T")[0];

      // Build provider info (may be empty array)
      const providerInfo: ProviderLoadedInfo[] = providers.map((p) => ({
        id: p.id,
        name: p.fullName,
        slots: countBookableSlots(p.availableSlots),
      }));

      // Build filters
      const filters = {
        specialties: selectedSpecialties,
        location: selectedLocation,
        date: currentDateStr,
        appointment_type: appointmentType,
      };

      // Determine trigger
      let trigger: ProvidersLoadedTrigger;

      if (isInitialLoadRef.current) {
        trigger = { type: "initial_load" };
        isInitialLoadRef.current = false;
      } else if (
        prevDateRef.current &&
        prevDateRef.current !== currentDateStr
      ) {
        trigger = {
          type: "date_change",
          previous_date: prevDateRef.current,
          new_date: currentDateStr,
        };
      } else if (
        prevSpecialtiesRef.current.length > 0 &&
        JSON.stringify(prevSpecialtiesRef.current) !==
          JSON.stringify(selectedSpecialties)
      ) {
        trigger = {
          type: "filter_change",
          filter_type: "specialty",
          previous_value: prevSpecialtiesRef.current,
          new_value: selectedSpecialties,
        };
      } else if (
        prevLocationRef.current &&
        prevLocationRef.current !== selectedLocation
      ) {
        trigger = {
          type: "filter_change",
          filter_type: "location",
          previous_value: prevLocationRef.current,
          new_value: selectedLocation,
        };
      } else {
        // Fallback
        trigger = { type: "initial_load" };
      }

      // Track with funnel tracker
      funnelTracker.trackProvidersLoaded(providerInfo, filters, trigger);

      // Update refs for next comparison
      prevDateRef.current = currentDateStr;
      prevSpecialtiesRef.current = [...selectedSpecialties];
      prevLocationRef.current = selectedLocation;
    }
  }, [
    providers,
    selectedDate,
    selectedSpecialties,
    selectedLocation,
    getAppointmentType,
    isLoading,
  ]);

  // Track when no availability is found for the next month
  useEffect(() => {
    if (appointmentCreatedRef.current) return;
    if (
      !isCalendarLoading &&
      availabilityForNextMonth === false &&
      hasInitializedRef.current
    ) {
      // Auto-switch to telehealth if currently in-person
      if (selectedLocation !== VIDEO_CALL_LOCATION) {
        setAutoSwitchedToTelehealth(true);
        setSelectedLocation(VIDEO_CALL_LOCATION);
        return;
      }

      const appointmentType = getAppointmentType();

      const filters = {
        specialties: selectedSpecialties,
        location: selectedLocation,
        date: "", // No date available
        appointment_type: appointmentType,
      };

      const trigger: ProvidersLoadedTrigger = isInitialLoadRef.current
        ? { type: "initial_load" }
        : {
            type: "filter_change",
            filter_type:
              prevLocationRef.current !== selectedLocation
                ? "location"
                : "specialty",
            previous_value:
              prevLocationRef.current !== selectedLocation
                ? prevLocationRef.current
                : prevSpecialtiesRef.current,
            new_value:
              prevLocationRef.current !== selectedLocation
                ? selectedLocation
                : selectedSpecialties,
          };

      funnelTracker.trackProvidersLoaded([], filters, trigger);

      if (selectedLocation !== VIDEO_CALL_LOCATION) {
        funnelTracker.trackAutoSwitchTelehealth(
          selectedLocation,
          "no_availability",
        );
        setAutoSwitchedToTelehealth(true);
        setSelectedLocation(VIDEO_CALL_LOCATION);
        return;
      }

      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }

      // Update refs
      prevSpecialtiesRef.current = [...selectedSpecialties];
      prevLocationRef.current = selectedLocation;
    }
  }, [
    availabilityForNextMonth,
    isCalendarLoading,
    selectedSpecialties,
    selectedLocation,
    getAppointmentType,
  ]);

  // Auto-switch to telehealth when in-person has no availability.
  //
  // Gate on isSuccess, NOT merely !isLoading: a disabled query (patient has no
  // conditions, so the providers query never runs) and an errored query BOTH
  // leave isLoading=false with providers=[]. Switching on those would fire a
  // spurious one-time switch to telehealth where the query is still disabled/
  // failing, stranding the user on a blank screen with a disabled Confirm
  // button. isSuccess is true only when the query actually executed and
  // returned an (empty) result — the real "in-person has no slots" case.
  useEffect(() => {
    if (appointmentCreatedRef.current) return;
    if (
      isSuccess &&
      selectedDate &&
      providers.length === 0 &&
      selectedLocation !== VIDEO_CALL_LOCATION
    ) {
      funnelTracker.trackAutoSwitchTelehealth(selectedLocation, "no_providers");
      setAutoSwitchedToTelehealth(true);
      setSelectedLocation(VIDEO_CALL_LOCATION);
    }
  }, [providers, isSuccess, selectedDate, selectedLocation]);

  const queryClient = useQueryClient();
  const createAppointmentMutation = useCreateAppointment();
  const { finalizeDraft } = useDraftSync();

  // ProviderCard stops rendering a slot once its start time passes, but the
  // selection lives up here — left alone, the card would show nothing selected
  // while Confirm stayed enabled on a time that can no longer be booked. Drop it
  // and say so. Skipped while a booking is in flight or already made, so a slot
  // whose time passes mid-request can't clear the state its handlers still read.
  const now = useNow();

  useEffect(() => {
    if (createAppointmentMutation.isPending || appointmentCreatedRef.current) {
      return;
    }

    if (selectedTime !== null && isSlotInPast(selectedTime, now)) {
      setSelectedTime(null);
      setSelectedProvider(null);
      toast.error("That time has already passed — please pick another slot.");
    }
  }, [now, selectedTime, createAppointmentMutation.isPending]);

  // One-shot guard so a booking token that is rejected even after we re-issue it
  // can't spin the recover -> retry -> recover loop. Reset once a booking
  // succeeds or the patient picks a different slot.
  const tokenRecoveryAttemptedRef = useRef(false);

  // True while recoverTokenAndBook is awaiting finalizeDraft(). During that
  // network round-trip createAppointmentMutation.isPending is still false, so
  // without this the Confirm button stays enabled with no loading state and a
  // second click re-enters handleConfirm, trips the one-shot guard, and
  // restarts the (finalized) session — the exact strand recovery exists to
  // prevent.
  const [isRecovering, setIsRecovering] = useState(false);

  const restartExpiredSession = () => {
    toast.error(
      "Your session has expired. Please start your sign-up again to book.",
    );
    useSignUpStore.getState().reset();
    useFunnelStore.getState().resetSession();
    navigate(ROUTES.signUp);
  };

  // The booking token is short-lived and can be missing (a session that predates
  // it) or expired server-side. Restarting the whole sign-up would send the
  // patient back to the email step, where the email-exists guard permanently
  // blocks them — their patient already exists in Healthie from finalize. So
  // instead re-issue a token by re-finalizing the still-valid draft and retry the
  // booking. Only fall back to a restart if that recovery itself fails.
  const recoverTokenAndBook = async () => {
    if (tokenRecoveryAttemptedRef.current) {
      restartExpiredSession();

      return;
    }
    tokenRecoveryAttemptedRef.current = true;
    setIsRecovering(true);

    try {
      const result = await finalizeDraft();

      if (!result.bookingToken) {
        restartExpiredSession();

        return;
      }

      setPatientField("bookingToken", result.bookingToken);
      submitBooking(result.bookingToken);
    } catch {
      restartExpiredSession();
    } finally {
      // submitBooking has fired the mutation by now, so isPending takes over the
      // button's disabled/loading state from here.
      setIsRecovering(false);
    }
  };

  const submitBooking = (bookingToken: string) => {
    if (!(selectedProvider && selectedDate && selectedTime)) return;

    const appointmentType = getAppointmentType();

    const isEmployerPPO =
      patient.primaryInsurance.planTypeProvider === "employer" &&
      patient.primaryInsurance.planCoverage === "PPO";

    createAppointmentMutation.mutate(
      {
        providerId: selectedProvider.id,
        dateTime: selectedTime,
        preferredOfficeVisit: selectedLocation ?? patient.preferredOfficeVisit,
        bookingToken,
        conditions: selectedSpecialties,
        utmParams: patient.utmParams ?? getUtmParams(),
        sendConversionEvent: isEmployerPPO,
      },
      {
        onSuccess: (data) => {
          appointmentCreatedRef.current = true;
          setAppointmentBooked(true);

          // Track funnel completion with full appointment data
          funnelTracker.trackFunnelComplete({
            provider_id: selectedProvider.id,
            provider_name: selectedProvider.fullName,
            appointment_date: selectedDate.toISOString().split("T")[0],
            appointment_time: selectedTime,
            appointment_type: appointmentType,
            location: data.location || selectedLocation,
            specialties: selectedSpecialties,
            providers_shown: providers.length,
            slots_available: countBookableSlots(
              selectedProvider.availableSlots,
            ),
          });

          setAppointmentId(data.id);
          setAppointmentLocation(data.location);
          setShowAppointmentCreated(true);
        },
        onError: (error: unknown) => {
          if (isExpiredTokenError(error)) {
            // Token was present but rejected server-side (expired past its TTL
            // or tampered). Try once to silently re-issue a fresh token from the
            // still-valid draft and retry; only restart if that fails.
            void recoverTokenAndBook();

            return;
          }

          if (isStaleSlotError(error)) {
            // The slot was taken between our availability check and this
            // booking. Refresh availability, clear the stale selection, and
            // ask the patient to pick again — instead of a generic error.
            tokenRecoveryAttemptedRef.current = false;
            queryClient.invalidateQueries({ queryKey: ["providers"] });
            setSelectedTime(null);
            setSelectedProvider(null);
            toast.error(getStaleSlotMessage(selectedTime));

            return;
          }

          toast.error(getBookingErrorMessage(error));
        },
      },
    );
  };

  const handleConfirm = () => {
    if (!(selectedProvider && selectedDate && selectedTime)) return;

    // A recovery finalize is already in flight; ignore the extra click so it
    // can't trip the one-shot guard and restart the session.
    if (isRecovering) return;

    // The booking token is the sole proof this session owns the patient. If it
    // never made it into the store (older session) recover one from the draft
    // rather than sending an empty token the backend would reject.
    const { bookingToken } = patient;

    if (!bookingToken) {
      void recoverTokenAndBook();

      return;
    }

    submitBooking(bookingToken);
  };

  return (
    <>
      <Toaster containerClassName="mt-16" />
      {showAppointmentCreated &&
      selectedProvider &&
      selectedTime &&
      selectedLocation ? (
        <AppointmentCreatedScreen
          provider={selectedProvider}
          appointmentDateTime={selectedTime}
          location={appointmentLocation}
          appointmentId={appointmentId}
        />
      ) : (
        // Custom layout for provider selection - not using MainLayout for better scroll control
        <div className="flex h-screen flex-col items-center bg-pink-100 sm:min-h-screen sm:py-8">
          {/* Main container - fills viewport on mobile */}
          <div className="flex h-full w-full max-w-[570px] flex-col overflow-hidden bg-white shadow-xl sm:h-auto sm:max-h-[calc(100vh-64px)] sm:rounded-lg">
            {/* Sticky header section */}
            <div className="shrink-0 border-b border-gray-100 bg-white px-4 pt-4 sm:px-6 sm:pt-6">
              {/* Logo and title */}
              <div className="mb-4 flex items-center gap-3">
                <HipaaLogo className="size-10 shrink-0 sm:size-12" />
                <h1 className="font-display text-base sm:text-lg">
                  Select a provider
                </h1>
              </div>

              {/* Filters */}
              <div className="mb-3 flex flex-row gap-2">
                <div
                  className={
                    patient.state === NEW_YORK_STATE ? "w-1/2" : "w-full"
                  }
                >
                  <SpecialtyDropdown
                    selectedSpecialties={selectedSpecialties}
                    onSpecialtyChange={handleSpecialtyChange}
                  />
                </div>
                {patient.state === NEW_YORK_STATE && (
                  <div className="w-1/2">
                    <LocationDropdown
                      selectedLocation={selectedLocation}
                      onLocationChange={handleLocationChange}
                    />
                  </div>
                )}
              </div>

              {/* Calendar */}
              <div className="pb-4">
                <CalendarSelector
                  month={currentMonth}
                  day={currentDay}
                  dayName={currentDayName}
                  daysInView={calendarDays}
                  onDateSelect={handleCalendarDateSelect}
                  onNavigatePrev={canNavigatePrev ? handleNavigatePrev : null}
                  onNavigateNext={handleNavigateNext}
                  selectedDate={selectedDate}
                  loading={isCalendarLoading}
                  isMobile={isMobile}
                />
              </div>
            </div>

            {/* Scrollable providers list */}
            <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 px-4 py-4 sm:px-6">
              {/* Status messages */}
              {!availabilityForNextMonth && !isCalendarLoading && (
                <div className="mb-4 rounded-lg bg-amber-50 p-3 text-center text-sm text-amber-800">
                  Thank you for completing the survey. We'll be in touch shortly
                  to schedule you with one of our Registered Dietitians!
                </div>
              )}

              {calendarError && (
                <div className="mb-4 text-xs text-red-500">
                  Error loading calendar
                </div>
              )}

              {isLoading && (
                <div className="py-8 text-center text-sm text-gray-500">
                  Loading providers...
                </div>
              )}

              {error && (
                <div className="mb-4 text-xs text-red-500">
                  Error loading providers
                </div>
              )}

              {/* Psst hint */}
              {!isCalendarLoading &&
                providers.length > 0 &&
                selectedLocation !== VIDEO_CALL_LOCATION && (
                  <div className="mb-3 text-xs text-gray-500">
                    Psst, it's easier to find availability with a{" "}
                    <button
                      onClick={() => handleLocationChange(VIDEO_CALL_LOCATION)}
                      className="cursor-pointer underline"
                    >
                      video call
                    </button>
                    .
                  </div>
                )}

              {/* Auto-switched message */}
              {autoSwitchedToTelehealth && providers.length > 0 && (
                <div className="mb-3 rounded-lg bg-blue-50 p-2 text-center text-xs text-blue-800">
                  In-person appointments are currently unavailable based on your
                  insurance and reason for consultation.<br></br> Telehealth
                  options are displayed below.
                </div>
              )}

              {createAppointmentMutation.isPending && (
                <div className="mb-3 text-xs text-gray-500">
                  Creating appointment...
                </div>
              )}

              {/* Provider cards */}
              <div className="flex flex-col gap-3">
                {providers.map((provider) => (
                  <ProviderCard
                    key={provider.id}
                    provider={provider}
                    location={selectedLocation}
                    onTimeSelect={(time) => handleTimeSelect(provider, time)}
                    selectedTime={
                      selectedProvider?.id === provider.id ? selectedTime : null
                    }
                  />
                ))}
              </div>

              {/* Results count */}
              {!isLoading && providers.length > 0 && (
                <div className="mt-4 text-center text-xs text-gray-400">
                  {providers.length} provider{providers.length !== 1 ? "s" : ""}{" "}
                  available
                </div>
              )}

              {/* Terminal empty state — prevents a blank, no-path-forward strand
                  when there is genuinely nothing to show and no auto-switch will
                  fire: already on telehealth with zero results, or the providers
                  query is disabled (no conditions selected). */}
              {!isLoading &&
                !error &&
                providers.length === 0 &&
                selectedDate &&
                (selectedLocation === VIDEO_CALL_LOCATION ||
                  selectedSpecialties.length === 0) && (
                  <div className="py-8 text-center text-sm text-gray-500">
                    No providers are available for this date. Please choose
                    another date.
                  </div>
                )}
            </div>

            {/* Sticky footer */}
            <div className="shrink-0 border-t border-gray-200 bg-green-200">
              <ConfirmActionButtons
                onConfirm={handleConfirm}
                disabled={
                  !selectedProvider ||
                  !selectedTime ||
                  !selectedDate ||
                  isRecovering
                }
                isLoading={createAppointmentMutation.isPending || isRecovering}
                helperText="Select a date and time slot to continue"
                selectedSummary={
                  selectedProvider && selectedTime
                    ? `${selectedProvider.fullName} · ${formatAppointmentTime(selectedTime)}`
                    : undefined
                }
              />
              {isNonProduction() && (sessionId || patient.groupName) && (
                <div className="px-4 pb-2 text-center font-mono text-xs text-gray-400">
                  {sessionId && <div>Session: {sessionId}</div>}
                  {patient.groupName && <div>Group: {patient.groupName}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProviderSelectionScreen;
